import React, { useState, useEffect } from "react";
import { Fieldset } from "primereact/fieldset";
import { ProgressSpinner } from "primereact/progressspinner";
import moment from "moment";
import { BillTariffModel } from "../../models/BillTariff";
import tariffApiService from "../../services/api/tariff/TariffApiService";

interface BillTariffVersionsListProps {
    billTypeCode: string;
    currentTariffId: number;
    visible: boolean;
}

const BillTariffVersionsList: React.FC<BillTariffVersionsListProps> = ({ 
    billTypeCode, 
    currentTariffId, 
    visible 
}) => {
    const [allVersions, setAllVersions] = useState<BillTariffModel[]>([]);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);

    useEffect(() => {
        if (visible && billTypeCode) {
            loadAllVersions();
        } else {
            setAllVersions([]);
        }
    }, [visible, billTypeCode]);

    const loadAllVersions = async () => {
        if (!billTypeCode) return;

        setIsLoadingVersions(true);
        try {
            // Fetch all tariffs and filter by BillTypeCode
            const result = await tariffApiService.getAllBillTariffs();
            if (result.success && result.data) {
                // Filter by BillTypeCode and sort by version descending
                const versions = result.data
                    .filter((t: BillTariffModel) => t.billTypeCode === billTypeCode)
                    .sort((a: BillTariffModel, b: BillTariffModel) => b.version - a.version);
                setAllVersions(versions);
            }
        } catch (error) {
            console.error("Error loading all versions:", error);
        } finally {
            setIsLoadingVersions(false);
        }
    };

    if (!billTypeCode) {
        return null;
    }

    return (
        <>
            <h5></h5>
            <Fieldset legend={`All Versions for Bill Type Code: ${billTypeCode}`} toggleable={true} collapsed={false}>
                <div style={{ 
                    border: "1px solid #dee2e6", 
                    borderRadius: "4px", 
                    padding: "1rem",
                    marginTop: "0.5rem"
                }}>
                    {isLoadingVersions ? (
                        <div style={{ textAlign: "center", padding: "2rem" }}>
                            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
                        </div>
                    ) : allVersions.length > 0 ? (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "1px solid #dee2e6" }}>
                                    <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Version</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Effective From</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Effective To</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Value</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allVersions.map((version) => {
                                    const isCurrentVersion = version.id === currentTariffId;
                                    return (
                                        <tr 
                                            key={version.id} 
                                            style={{ 
                                                borderBottom: "1px solid #dee2e6",
                                                backgroundColor: isCurrentVersion ? "#e7f3ff" : "transparent"
                                            }}
                                        >
                                            <td style={{ padding: "0.5rem", fontWeight: isCurrentVersion ? "bold" : "normal" }}>
                                                {version.version} {isCurrentVersion && "(Current)"}
                                            </td>
                                            <td style={{ padding: "0.5rem" }}>
                                                {version.effectiveFrom 
                                                    ? moment(version.effectiveFrom).format("DD/MM/YYYY")
                                                    : "-"}
                                            </td>
                                            <td style={{ padding: "0.5rem" }}>
                                                {version.effectiveTo 
                                                    ? moment(version.effectiveTo).format("DD/MM/YYYY")
                                                    : "-"}
                                            </td>
                                            <td style={{ padding: "0.5rem" }}>
                                                {version.tariffType === "SCALE" ? "Scale Tiers" : version.value}
                                            </td>
                                            <td style={{ padding: "0.5rem" }}>
                                                {version.status ? "Active" : "Inactive"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: "center", padding: "1rem" }}>No versions found</div>
                    )}
                </div>
            </Fieldset>
        </>
    );
};

export default BillTariffVersionsList;

